/**
 * @fileoverview Searches → Web search tab. Run an AI web search report and
 * browse the history of past runs. Reports are saved like InfoTrack searches.
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import { Loader2, Search } from "lucide-react";
import { DocumentDetail } from "@/components/jobs/phases/gather/documents/DocumentDetail";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { WebSearchReportCard } from "./-ui.web-search-report-card";

const GROUP_CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] overflow-hidden";

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/jobs/$jobId/searches/web",
)({
  component: JobWebSearchPage,
});

function JobWebSearchPage() {
  const { jobId } = Route.useParams();

  const { data: job } = useQuery(
    convexQuery(api.jobs.queries.getJob, { jobId: jobId as Id<"jobs"> }),
  );
  const { data: reports } = useQuery(
    convexQuery(api.jobs.webSearch.queries.listWebSearchReportsForJob, {
      jobId: jobId as Id<"jobs">,
    }),
  );

  const createReport = useMutation(
    api.jobs.webSearch.mutations.createWebSearchReport,
  );

  const [focus, setFocus] = useState("");
  const [running, setRunning] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState<Id<"documents"> | null>(
    null,
  );

  const isViewer = job?.userRole === "viewer";
  const latest = reports?.[0];
  const history = (reports ?? []).slice(1);

  const handleRun = async () => {
    setRunning(true);
    try {
      await createReport({
        jobId: jobId as Id<"jobs">,
        query: focus.trim() || undefined,
      });
      setFocus("");
      toast.success("Web search started.");
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Could not start web search.",
      );
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="flex flex-1 flex-col gap-5 bg-muted p-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Web search</h1>
        <p className="text-sm text-muted-foreground">
          Morgan AI searches the web for background on{" "}
          {job?.entityName ?? "this entity"} and writes a report, saved to the
          job like a search.
        </p>
      </div>

      {!isViewer ? (
        <div className={GROUP_CARD_CLASS}>
          <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-1.5">
              <label htmlFor="web-focus" className="text-sm font-medium">
                Optional focus
              </label>
              <Input
                id="web-focus"
                placeholder="e.g. director litigation, related companies, recent news"
                value={focus}
                onChange={(e) => setFocus(e.target.value)}
              />
            </div>
            <Button onClick={handleRun} disabled={running}>
              {running ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Search className="size-4" />
              )}
              Run web search
            </Button>
          </div>
        </div>
      ) : null}

      {latest ? (
        <WebSearchReportCard
          report={{
            ...latest,
            _id: latest._id,
            resultDocumentId: latest.resultDocumentId ?? null,
          }}
          onViewPdf={(id) => setSelectedDocId(id as Id<"documents">)}
        />
      ) : reports ? (
        <div className="text-sm text-muted-foreground">
          No web searches yet.
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">Loading…</div>
      )}

      {history.length ? (
        <>
          <h2 className="pt-2 text-lg font-semibold tracking-tight">
            Previous reports
          </h2>
          <div className="space-y-3">
            {history.map((r) => (
              <WebSearchReportCard
                key={r._id}
                report={{
                  ...r,
                  _id: r._id,
                  resultDocumentId: r.resultDocumentId ?? null,
                }}
                onViewPdf={(id) => setSelectedDocId(id as Id<"documents">)}
              />
            ))}
          </div>
        </>
      ) : null}

      <DocumentDetail
        documentId={selectedDocId}
        open={selectedDocId !== null}
        isViewer={!!isViewer}
        onClose={() => setSelectedDocId(null)}
        defaultTab="original"
      />
    </div>
  );
}
